"use client";
import React, { useState, useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useIsMobile } from "@/utils/useIsMobile";
import { SchemeReturn } from "@/type/schemeType";

type PropData = {
  data: SchemeReturn;
};

const TrailingRetrunsChart = ({ data }: PropData) => {
  const isMobile = useIsMobile();
  const [isShortTerm, setIsShortTerm] = useState(true);

  const { categories, fundData, benchmarkData, categoryAvgData } = useMemo(() => {
    if (!data) return { categories: [], fundData: [], benchmarkData: [], categoryAvgData: [] };

    const shortTerm = {
      categories: ["1W", "1M", "3M", "6M"],
      fund: [data.sr_one_weak_abs, data.sr_one_month_abs, data.sr_three_month_abs, data.sr_six_month_abs],
      benchmark: [data.br_one_weak_abs, data.br_one_month_abs, data.br_three_month_abs, data.br_six_month_abs],
      category: [data.cr_one_weak_abs, data.cr_one_month_abs, data.cr_three_month_abs, data.cr_six_month_abs],
    };

    const longTerm = {
      categories: ["1Y", "2Y", "3Y", "5Y"],
      fund: [data.sr_one_year_abs, data.sr_two_year, data.sr_three_year, data.sr_five_year],
      benchmark: [data.br_one_year_abs, data.br_two_year, data.br_three_year, data.br_five_year],
      category: [data.cr_one_year_abs, data.cr_two_year, data.cr_three_year, data.cr_five_year],
    };

    const selected = isShortTerm ? shortTerm : longTerm;

    return {
      categories: selected.categories,
      fundData: selected.fund,
      benchmarkData: selected.benchmark,
      categoryAvgData: selected.category,
    };
  }, [data, isShortTerm]);

  const allValues = [...fundData, ...benchmarkData, ...categoryAvgData];
  const minValue = Math.min(...allValues);

  const options: Highcharts.Options = {
    chart: {
      type: "column",
      height: 400,
      style: {
        fontFamily: "'Inter', sans-serif",
      },
    },
    title: {
      text: "",
    },
    xAxis: {
      categories,
      title: {
        text: "",
      },
      labels: {
        style: {
          fontSize: "14px",
        },
        step: 1,
        reserveSpace: true,
      },
    },
    yAxis: {
      visible: !isMobile || isShortTerm,
      min: minValue < 0 ? minValue : 0,
      title: {
        text: undefined,
      },
      labels: {
        format: "{value}%",
      },
    },
    legend: {
      align: "right",
      verticalAlign: "middle",
      layout: "vertical",
    },
    tooltip: {
      pointFormat: "<span style='color:{series.color}'>●</span> {series.name}: <b>{point.y}%</b><br/>",
    },
    plotOptions: {
      column: {
        grouping: true,
        groupPadding: 0.1,
        pointPadding: 0,
        borderWidth: 0,
        pointWidth: 35,
        dataLabels: {
          enabled: true,
          inside: !isMobile,
          align: "center",
          verticalAlign: isMobile ? "bottom" : "top",
          y: isMobile ? -5 : 0,
          formatter: function () {
            const decimals = isMobile ? 2 : 1;
            return `${this.y?.toFixed(decimals) ?? "0.0"}%`;
          },
          style: {
            color: isMobile ? "#000" : "#fff",
            fontSize: "10px",
            fontWeight: "500",
            textOutline: "none",
          },
        },
      },
    },
    series: [
      {
        name: "Fund",
        type: "column",
        data: fundData,
        color: "#004DC9",
      },
      {
        name: "Benchmark",
        type: "column",
        data: benchmarkData,
        color: "#29A051",
      },
      {
        name: "Category Average",
        type: "column",
        data: categoryAvgData,
        color: "#BABABA",
      },
    ],
    credits: {
      enabled: false,
    },
    responsive: {
      rules: isMobile
        ? [
            {
              condition: {
                maxWidth: 750,
              },
              chartOptions: {
                plotOptions: {
                  column: {
                    pointWidth: 25,
                  },
                },
                legend: {
                  layout: "horizontal",
                  align: "center",
                  verticalAlign: "bottom",
                },
              },
            },
            {
              condition: {
                maxWidth: 400,
              },
              chartOptions: {
                yAxis: {
                  visible: false,
                },
                plotOptions: {
                  column: {
                    groupPadding: 0.14,
                    pointWidth: 17,
                  },
                },
              },
            },
          ]
        : [],
    },
  };

  const termOptions = [
    { label: "Short Term", value: true },
    { label: "Long Term", value: false },
  ];

  return (
    <>
      <div className="h-400">
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>

      <div className="flex gap-1 sticky bottom-0 z-1 w-full max-w-85 rounded-full bg-[#EBF3FF] text-sm sm:text-base border border-[#1A51AA24] p-0.5 mx-auto">
        {termOptions.map((option) => (
          <button key={option.label} onClick={() => setIsShortTerm(option.value)} className={`block w-1/2 px-2.5 sm:px-4 py-1 rounded-full transition ${isShortTerm === option.value ? "bg-white text-primaryBlue shadow-xs" : "text-[#202020A6] hover:text-primaryBlue hover:bg-[#ffffff8f]"}`}>
            {option.label}
          </button>
        ))}
      </div>
    </>
  );
};

export default TrailingRetrunsChart;
